// Data for /tools/best-ai-sdr-for-b2b-saas/
// Owner: Dmitrii Serg
// Prices verified from vendor sites. May 2026

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TldrRow {
  toolSlug: string;
  toolName: string;
  bestFor: string;
  motion: "Inbound" | "Outbound" | "Hybrid";
  priceLabel: string;
  freeTrial: boolean;
  g2Rating: string;
  featured: boolean;
  profileUrl?: string;
  externalUrl?: string;
}

export interface CriterionCard {
  n: number;
  title: string;
  body: string;
}

export interface IoRow {
  name: string;
  task: string;
  inputSignals: string;
  keyTools: string;
  bestFor: string;
}

export interface InboundOutbound {
  inbound: IoRow[];
  outbound: IoRow[];
}

export interface PricingFacts {
  pricingModel: string;
  freeTrial: string;
  enterpriseNote?: string;
}

export interface TopTool {
  position: number;
  toolSlug: string;
  toolName: string;
  bestFor: string;
  intro: string;
  pros: string[];
  cons: string[];
  startingPrice: string;
  pricingFacts: PricingFacts;
  outcomesLine?: string;
  g2Rating: string;
  profileUrl: string;
}

export interface MoreTool {
  toolSlug: string;
  toolName: string;
  motion: "Inbound" | "Outbound" | "Hybrid";
  tagline: string;
  startingPrice: string;
  g2Rating: string;
  pros: string[];
  cons: string[];
  bestFor: string;
  profileUrl?: string; // internal /tools/…/ path; omit if no Dashly profile
}

export interface FreeTool {
  name: string;
  type: "Free tier" | "Open source";
  description: string;
  limitations: string[];
  bestFor: string;
}

export interface StageCard {
  stage: string;
  label: string;
  color: string;
  heading: string;
  body: string;
  tools: string[];
  watch_out: string;
}

export interface FaqItem {
  question: string;
  answer: string;
}

// ─── TL;DR rows (10 tools) ────────────────────────────────────────────────────

export const TLDR_ROWS: TldrRow[] = [
  {
    toolSlug: "dashly",
    toolName: "Dashly",
    bestFor: "Inbound AI SDR for trial users",
    motion: "Inbound",
    priceLabel: "$500/mo",
    freeTrial: false,
    g2Rating: "4.8",
    featured: true,
    profileUrl: "/tools/ai-inbound-agents/dashly/",
  },
  {
    toolSlug: "qualified",
    toolName: "Qualified (Piper)",
    bestFor: "Enterprise inbound on Salesforce",
    motion: "Inbound",
    priceLabel: "Custom",
    freeTrial: false,
    g2Rating: "4.9",
    featured: false,
    profileUrl: "/tools/conversational-marketing/qualified/",
  },
  {
    toolSlug: "aisdr",
    toolName: "AiSDR",
    bestFor: "Autonomous outbound email",
    motion: "Outbound",
    priceLabel: "From $900/mo",
    freeTrial: false,
    g2Rating: "4.7",
    featured: false,
    profileUrl: "/tools/ai-sdr/aisdr/",
  },
  {
    toolSlug: "11x-ai",
    toolName: "11x.ai (Alice)",
    bestFor: "Fully autonomous outbound at scale",
    motion: "Outbound",
    priceLabel: "Custom",
    freeTrial: false,
    g2Rating: "4.3",
    featured: false,
    profileUrl: "/tools/ai-sdr/11x/",
  },
  {
    toolSlug: "artisan",
    toolName: "Artisan (Ava)",
    bestFor: "Outbound BDR with deep research",
    motion: "Outbound",
    priceLabel: "Custom",
    freeTrial: false,
    g2Rating: "4.4",
    featured: false,
    profileUrl: "/tools/ai-sdr/artisan/",
  },
  {
    toolSlug: "reply-io",
    toolName: "Reply.io (Jason AI)",
    bestFor: "Multi-channel outreach + sequences",
    motion: "Hybrid",
    priceLabel: "From $49/mo",
    freeTrial: true,
    g2Rating: "4.6",
    featured: false,
  },
  {
    toolSlug: "salesforge",
    toolName: "Salesforge (Agent Frank)",
    bestFor: "Outbound email with deliverability infra",
    motion: "Outbound",
    priceLabel: "From $416/mo",
    freeTrial: false,
    g2Rating: "4.3",
    featured: false,
    profileUrl: "/tools/ai-sdr/salesforge/",
  },
  {
    toolSlug: "apollo-io",
    toolName: "Apollo.io",
    bestFor: "Prospecting database + outreach",
    motion: "Outbound",
    priceLabel: "From $49/mo",
    freeTrial: true,
    g2Rating: "4.8",
    featured: false,
  },
  {
    toolSlug: "clay",
    toolName: "Clay (Claygent)",
    bestFor: "Data enrichment + hyper-personalized outbound",
    motion: "Outbound",
    priceLabel: "From $149/mo",
    freeTrial: false,
    g2Rating: "4.9",
    featured: false,
  },
  {
    toolSlug: "intercom-fin",
    toolName: "Intercom Fin",
    bestFor: "SaaS-native inbound chat",
    motion: "Inbound",
    priceLabel: "From $29/mo",
    freeTrial: true,
    g2Rating: "4.5",
    featured: false,
    profileUrl: "/tools/customer-messaging/intercom/",
  },
];

// ─── Evaluation criteria cards ────────────────────────────────────────────────

export const CRITERIA: CriterionCard[] = [
  {
    n: 1,
    title: "ICP qualification logic",
    body: "Does the tool qualify against your actual criteria during the conversation, or does it collect an email and stop? Context-aware qualification is the difference between a lead routed correctly and a lead lost to follow-up delay.",
  },
  {
    n: 2,
    title: "CRM and SaaS stack integration",
    body: "HubSpot, Salesforce, Pipedrive sync. Bonus: Segment, Mixpanel, Stripe webhooks for product-led teams. A tool that does not write back to your CRM adds a manual data layer you will regret at scale.",
  },
  {
    n: 3,
    title: "Pricing fit for your stage",
    body: "The $900+/mo floor for autonomous outbound agents is justified at Series A. It is not justified at Seed. Evaluate pricing against your current pipeline volume, not the volume you plan to have in 12 months.",
  },
  {
    n: 4,
    title: "Channel coverage",
    body: "Website chat, cold email, LinkedIn. Check whether the tool covers your actual traffic source, not just the one it was built for. Buying a LinkedIn-first outbound tool when 90% of your pipeline is inbound is misaligned spend.",
  },
  {
    n: 5,
    title: "Setup time for a lean team",
    body: "Hours to first qualified lead. A 2-week warm-up before you see results is a real cost that does not show on the invoice. Ask vendors for median time-to-first-qualified-lead, not time-to-launch.",
  },
  {
    n: 6,
    title: "Contract flexibility",
    body: "Month-to-month vs. annual lock-in matters most at Seed and Series A, where the ICP is still moving. A tool that only sells annual contracts at $900+/mo is a bad fit before you have 90 days of conversion data to justify it.",
  },
  {
    n: 7,
    title: "Multilingual capability",
    body: "SaaS teams selling into the EU or LATAM need qualification conversations in the visitor's language, not just English. Check whether the AI agent's language coverage matches your actual traffic mix, not just your homepage's default locale.",
  },
];

// ─── Inbound vs outbound comparison ──────────────────────────────────────────

export const INBOUND_OUTBOUND: InboundOutbound = {
  inbound: [
    {
      name: "Inbound AI SDR",
      task: "Qualify and route website visitors and trial signups",
      inputSignals: "Page visits, time on site, CRM state, behavioral timeline",
      keyTools: "Dashly, Qualified, Intercom Fin",
      bestFor: "Teams with existing inbound traffic converting 1-5% of visitors",
    },
  ],
  outbound: [
    {
      name: "Outbound AI SDR",
      task: "Research cold prospects, write personalized sequences, book meetings",
      inputSignals: "ICP match, company news, LinkedIn activity, intent data",
      keyTools: "AiSDR, 11x.ai, Artisan, Salesforge",
      bestFor: "Teams expanding into new segments or running account-based campaigns",
    },
  ],
};

// ─── Top 3 full tool cards ────────────────────────────────────────────────────

export const TOP_3_TOOLS: TopTool[] = [
  {
    position: 1,
    toolSlug: "dashly",
    toolName: "Dashly",
    bestFor: "Best inbound AI SDR for B2B SaaS teams",
    intro:
      "Dashly's AI agent qualifies website visitors and SaaS trial signups against your ICP in real time, then routes high-fit leads to a booked meeting before they bounce. Dashly customers report 82% conversation-to-meeting conversion (InfluADS, 90-day case study) and 60-90% meeting show-up rates. The agent runs on a native customer data platform with 30+ behavioral events, so every conversation has full lead context from session one.",
    pros: [
      "Responds to inbound leads in under 30 seconds, before intent fades and before a competitor chat catches the same visitor.",
      "Native CDP gives the agent full behavioral context: pages visited, features used, CRM stage, intent score. No manual setup.",
      "AI Booker runs inside the same chat, so qualification-to-meeting takes under 2 minutes without a human handoff.",
      "Covers the full inbound lifecycle: qualify, book, nurture, route to AE. No additional tools needed.",
    ],
    cons: [
      "Works best on sites with over 5,000 monthly visitors; teams with lower traffic see slower ROI (per G2 reviews).",
      "No outbound cold email built in; requires pairing with AiSDR or Reply.io for cold prospecting (per G2 reviews).",
    ],
    startingPrice: "$500/mo",
    pricingFacts: {
      pricingModel: "Seat-based + feature tiers",
      freeTrial: "14-day free trial",
      enterpriseNote: "Custom for enterprise volume",
    },
    outcomesLine:
      "82% conversation-to-meeting at InfluADS over 90 days. 60-90% show-up rate across B2B SaaS customers.",
    g2Rating: "4.8 (450+ reviews)",
    profileUrl: "/tools/ai-inbound-agents/dashly/",
  },
  {
    position: 2,
    toolSlug: "qualified",
    toolName: "Qualified (Piper)",
    bestFor: "Best for Salesforce-native SaaS teams",
    intro:
      "Qualified's Piper AI agent surfaces high-intent buyers in real time during their website visit, scores them against ABM target lists, and alerts AEs with a live notification. The agent runs natively inside Salesforce Marketing Cloud, so all activity writes directly to your existing CRM without a sync layer.",
    pros: [
      "Salesforce-native, so data stays in your CRM without a new integration layer or sync delay.",
      "Live AE alert fires when a target account is on-site, so a rep can open a conversation within seconds of intent.",
      "Highest G2 rating in this directory (4.9); buyers consistently flag intent scoring accuracy and AE routing speed.",
    ],
    cons: [
      "Requires Salesforce. Teams on HubSpot or a different CRM cannot use it (per G2 reviews).",
      "Custom enterprise pricing typically starts around $3,500/mo, putting it out of reach for Seed or early Series A (per G2 reviews).",
      "Onboarding runs 6-8 weeks on average; teams with tight timelines find the implementation slow (per G2 reviews).",
    ],
    startingPrice: "Custom",
    pricingFacts: {
      pricingModel: "Custom enterprise",
      freeTrial: "Demo only",
      enterpriseNote: "Typically $3,500+/mo",
    },
    g2Rating: "4.9 (700+ reviews)",
    profileUrl: "/tools/conversational-marketing/qualified/",
  },
  {
    position: 3,
    toolSlug: "aisdr",
    toolName: "AiSDR",
    bestFor: "Best for autonomous outbound email",
    intro:
      "AiSDR researches each prospect, writes a first email that references recent company news or LinkedIn activity, sends the full sequence, and handles reply triage with no SDR in the loop. Reviewers cite 80%+ of outreach openers referencing specific prospect context, which moves reply rates above industry average cold email benchmarks.",
    pros: [
      "Personalizes each message from public prospect data, not a template. Reviewers cite 80%+ of openers with specific company context.",
      "Built-in deliverability infrastructure: domain warm-up, sender rotation, bounce monitoring, inbox placement testing.",
      "HubSpot and Salesforce bidirectional sync keeps CRM updated without manual data entry after each reply.",
    ],
    cons: [
      "Email-only channel. Teams relying on LinkedIn DMs or phone touchpoints need a separate tool for those channels (per G2 reviews).",
      "$900/mo floor puts it out of reach for Seed-stage teams or solo operators (per G2 reviews).",
      "Personalization logic is fixed to platform models; custom messaging frameworks require workarounds (per G2 reviews).",
    ],
    startingPrice: "From $900/mo",
    pricingFacts: {
      pricingModel: "Usage-based + agent seats",
      freeTrial: "Free trial available",
    },
    g2Rating: "4.7 (300+ reviews)",
    profileUrl: "/tools/ai-sdr/aisdr/",
  },
];

// ─── More tools (7 lighter cards) ────────────────────────────────────────────

export const MORE_TOOLS: MoreTool[] = [
  {
    toolSlug: "11x-ai",
    toolName: "11x.ai (Alice)",
    motion: "Outbound",
    tagline:
      "Fully autonomous outbound SDR that researches, writes, and sequences across email and LinkedIn without a human.",
    startingPrice: "Custom ($5K+/mo)",
    g2Rating: "4.3",
    pros: [
      "Handles the entire outbound workflow autonomously: prospect research, email writing, sequence management, and reply triage.",
      "Multi-channel coverage: email + LinkedIn in a single workflow.",
    ],
    cons: [
      "$5K+/mo floor excludes Seed and early Series A teams (per G2 reviews).",
      "Onboarding typically takes 3+ weeks before the first sequences go live (per G2 reviews).",
    ],
    bestFor:
      "Series B+ SaaS with a verified ICP and budget for fully autonomous outbound at scale",
    profileUrl: "/tools/ai-sdr/11x/",
  },
  {
    toolSlug: "artisan",
    toolName: "Artisan (Ava)",
    motion: "Outbound",
    tagline:
      "Outbound AI BDR that combines deep prospect research with multi-touch email sequences.",
    startingPrice: "Custom",
    g2Rating: "4.4",
    pros: [
      "Deep signal-based personalization pulls from company news, job postings, and LinkedIn activity before writing.",
      "Multi-touch orchestration handles email sequences across 3-5 touchpoints automatically.",
    ],
    cons: [
      "Pricing is custom and opaque, with no public starting price on the website (per G2 reviews).",
      "Outbound email only; no inbound qualification or website chat (per G2 reviews).",
    ],
    bestFor:
      "Series A+ SaaS teams that want outbound with research quality above basic templates",
    profileUrl: "/tools/ai-sdr/artisan/",
  },
  {
    toolSlug: "reply-io",
    toolName: "Reply.io (Jason AI)",
    motion: "Hybrid",
    tagline:
      "Multi-channel outreach platform with an AI SDR agent that writes sequences and handles LinkedIn touchpoints.",
    startingPrice: "From $49/mo",
    g2Rating: "4.6",
    pros: [
      "Most accessible price in this list; a solo founder or 2-person sales team can afford it.",
      "Covers email + LinkedIn + calls in one platform without stitching three tools together.",
    ],
    cons: [
      "Sequence logic requires manual setup; the AI writes copy but does not design cadences autonomously (per G2 reviews).",
      "AI personalization quality is basic compared to purpose-built agents like AiSDR or Artisan (per G2 reviews).",
    ],
    bestFor:
      "Small SaaS sales teams that want multi-channel outreach without the $900+/mo floor",
  },
  {
    toolSlug: "salesforge",
    toolName: "Salesforge (Agent Frank)",
    motion: "Outbound",
    tagline:
      "Cold email platform built around deliverability infrastructure: unlimited warm-up, sender rotation, and inbox placement monitoring.",
    startingPrice: "From $416/mo",
    g2Rating: "4.3",
    pros: [
      "Unlimited domain warm-up and sender rotation keeps deliverability high even at 1,000+ emails per day.",
      "Inbox placement testing before a sequence goes live reduces hard bounces.",
    ],
    cons: [
      "Email-only channel; no LinkedIn or chat touchpoints (per G2 reviews).",
      "No inbound qualification; this is an outbound-only tool (per G2 reviews).",
    ],
    bestFor:
      "SaaS teams running high-volume cold email where deliverability is the primary bottleneck",
    profileUrl: "/tools/ai-sdr/salesforge/",
  },
  {
    toolSlug: "apollo-io",
    toolName: "Apollo.io",
    motion: "Outbound",
    tagline:
      "Prospecting database of 270M+ contacts combined with built-in email sequences and CRM sync.",
    startingPrice: "From $49/mo",
    g2Rating: "4.8",
    pros: [
      "270M+ contact database with AI-powered filtering by job title, industry, tech stack, and funding stage.",
      "Built-in sequence tool means you can find prospects and reach out without switching platforms.",
    ],
    cons: [
      "AI personalization is weaker than purpose-built agents; messages trend toward templates (per G2 reviews).",
      "Data freshness issues: contact details for fast-changing startups can be 6-12 months stale (per G2 reviews).",
    ],
    bestFor:
      "SaaS teams that need both a prospect database and basic outreach in one tool, at a price that works pre-Series A",
  },
  {
    toolSlug: "clay",
    toolName: "Clay (Claygent)",
    motion: "Outbound",
    tagline:
      "Data enrichment tool that pulls from 50+ sources and feeds AI-drafted personalized messages at the account level.",
    startingPrice: "From $149/mo",
    g2Rating: "4.9",
    pros: [
      "50+ data sources with waterfall enrichment: if one source misses a field, the next one fills it.",
      "Claygent AI drafts personalized messages based on the enrichment data, not a fixed template.",
    ],
    cons: [
      "Steep learning curve; most teams spend 2-4 weeks before their first workflow produces usable output (per G2 reviews).",
      "Requires RevOps or ops support to set up correctly, making it unsuitable for solo sellers (per G2 reviews).",
    ],
    bestFor:
      "RevOps-equipped SaaS teams doing high-IQ account-based outbound with custom data logic",
  },
  {
    toolSlug: "intercom-fin",
    toolName: "Intercom Fin",
    motion: "Inbound",
    tagline:
      "SaaS-native inbound AI agent that handles support and qualification conversations inside Intercom's existing platform.",
    startingPrice: "From $29/mo",
    g2Rating: "4.5",
    pros: [
      "Native to SaaS products. Plugs into an existing Intercom workspace without a new tool or integration.",
      "AI Copilot assists human agents in real time, reducing response time without replacing them.",
    ],
    cons: [
      "Pipeline routing is limited; the tool qualifies and resolves but does not book meetings or route to AEs natively (per G2 reviews).",
      "No outbound capabilities; this is a support-adjacent inbound tool (per G2 reviews).",
    ],
    bestFor:
      "SaaS teams already on Intercom who want AI-powered qualification inside their existing support chat",
    profileUrl: "/tools/customer-messaging/intercom/",
  },
];

// ─── Free tool options ────────────────────────────────────────────────────────

export const FREE_TOOLS: FreeTool[] = [
  {
    name: "HubSpot Chatflows",
    type: "Free tier",
    description:
      "HubSpot's free chatbot builder creates inbound qualification flows that collect visitor information and route to a human rep. It runs inside HubSpot's free CRM, so all conversations log automatically.",
    limitations: [
      "No AI qualification logic; flows are rule-based, not context-aware.",
      "No meeting booking; visitors route to a rep manually.",
      "No behavioral scoring or intent signals.",
    ],
    bestFor:
      "Pre-revenue or Seed teams already on HubSpot CRM who want basic inbound capture before investing in AI qualification.",
  },
  {
    name: "Botpress",
    type: "Open source",
    description:
      "Botpress is an open-source conversational AI platform that runs self-hosted on your infrastructure. It supports custom qualification flows, CRM integrations via API, and multi-channel deployment.",
    limitations: [
      "Requires engineering support to build, deploy, and maintain flows.",
      "No out-of-the-box SaaS ICP qualification logic; starts from a blank state.",
      "Ongoing maintenance cost as models and integrations change.",
    ],
    bestFor:
      "Engineering-led SaaS teams that want full control over their AI agent without a SaaS vendor dependency.",
  },
];

// ─── Stage guide ──────────────────────────────────────────────────────────────

export const STAGE_GUIDE: StageCard[] = [
  {
    stage: "seed",
    label: "Pre-revenue / Seed",
    color: "#d0ff37",
    heading: "Start with inbound. Skip cold outbound.",
    body: "Before you have product-market fit, outbound AI SDR burns budget on an ICP you have not validated. Focus on converting the website traffic and trial signups you already have. HubSpot Chatflows or Dashly's starter plan covers this without a $900/mo commitment.",
    tools: [
      "HubSpot Chatflows (free)",
      "Dashly starter plan",
      "Reply.io (for first cold experiments)",
    ],
    watch_out:
      "Avoid: AiSDR, 11x.ai, Artisan. The floor price requires pipeline volume you do not have yet.",
  },
  {
    stage: "series-a",
    label: "Series A",
    color: "#ff6b35",
    heading: "Layer in outbound once ICP is validated.",
    body: "At Series A you have a defined ICP and enough inbound to test conversion. Add an outbound AI SDR for new segment expansion while optimizing inbound with a dedicated tool. Budget $500-$2K/mo for the combined stack.",
    tools: [
      "Dashly or Qualified for inbound",
      "AiSDR or Reply.io for outbound",
      "Apollo.io if you need a prospect database",
    ],
    watch_out:
      "Avoid: Clay and 11x.ai until you have a RevOps person to run them properly.",
  },
  {
    stage: "series-b",
    label: "Series B and beyond",
    color: "#4c6fff",
    heading: "Run both motions at scale with specialized tools.",
    body: "At Series B you can justify enterprise outbound agents and account-based inbound tools. The combination of Dashly or Qualified for inbound plus 11x.ai or Artisan for outbound covers the full pipeline without a single all-in-one compromise.",
    tools: [
      "Qualified for enterprise ABM inbound (if on Salesforce)",
      "11x.ai or Artisan for high-volume outbound",
      "Clay for intent-based hyper-personalized campaigns",
    ],
    watch_out:
      "Evaluate TCO carefully: 11x.ai + Artisan custom pricing can reach $10K+/mo when combined.",
  },
];

// ─── FAQ items ────────────────────────────────────────────────────────────────

export const FAQ_ITEMS: FaqItem[] = [
  {
    question: "What is an AI SDR for B2B SaaS?",
    answer:
      "An AI SDR is a software agent that handles the prospecting, qualification, and meeting-booking work that human SDRs typically do. For B2B SaaS teams specifically, this covers two workflows: qualifying website visitors and trial signups (inbound), and researching and reaching out to cold prospects matching your ICP (outbound). Unlike sales automation, AI SDRs generate decisions from context, not pre-written rules. The conversation adapts based on the lead's behavior, CRM state, and real-time signals.",
  },
  {
    question: "How much does an AI SDR cost for a SaaS startup?",
    answer:
      "Entry-level tools start at $29/mo (Intercom Fin) and $49/mo (Apollo.io, Reply.io). Purpose-built autonomous agents like AiSDR start at $900/mo. Enterprise platforms like Qualified and 11x.ai are custom-priced, typically $3,500/mo and up. Budget $500 to $2K/mo for a functional inbound plus outbound AI SDR stack at Series A. Factor in 20 to 40 hours of data prep and setup time that do not appear on the invoice but represent real cost.",
  },
  {
    question: "Can an AI SDR handle inbound SaaS trial signups?",
    answer:
      "Yes. Inbound AI SDR tools like Dashly qualify trial signups in real time by scoring them against your ICP during a chat conversation. A trial user who hits the pricing page triggers the AI agent, which qualifies role, team size, and use case in 30 to 60 seconds, then books a meeting with the right AE if they fit. Dashly customers report 82% conversation-to-meeting conversion on this workflow. The agent needs access to the user's behavioral timeline to personalize the qualification.",
  },
  {
    question: "What is the best AI SDR for early-stage SaaS startups?",
    answer:
      "For Seed-stage teams, start with Dashly's starter plan for inbound qualification. It covers website visitors and trial signups without committing to $900+/mo outbound tools before you have a validated ICP. Add HubSpot Chatflows (free) if you are already on HubSpot. Avoid enterprise outbound tools like 11x.ai or Artisan at this stage. The ROI requires a verified ICP, a healthy email domain reputation, and enough pipeline volume to justify the floor price.",
  },
  {
    question: "What AI SDR tools integrate with HubSpot and Salesforce?",
    answer:
      "Most tools in this directory offer HubSpot and Salesforce integration. Dashly, AiSDR, Reply.io, Salesforge, and Apollo.io provide native bidirectional sync. Qualified is Salesforce-only. Clay connects to both via API but requires configuration work. Intercom offers native HubSpot and Salesforce integrations. For SaaS teams using Segment or Mixpanel alongside a CRM, check integration depth separately. Most tools sync CRM records but do not natively read product usage events from Segment.",
  },
  {
    question: "What is the difference between inbound and outbound AI SDR?",
    answer:
      "Inbound AI SDR tools (Dashly, Qualified, Intercom Fin) work on traffic that has already found you: website visitors, trial signups, demo requests. Their job is to qualify intent and route to the right next step before leads bounce. Outbound AI SDR tools (AiSDR, 11x.ai, Artisan) prospect cold lists that match your ICP and send personalized sequences without human input. Most B2B SaaS teams need both. Inbound capitalizes on existing high-intent traffic. Outbound reaches segments where inbound does not.",
  },
  {
    question: "Is Dashly an AI SDR?",
    answer:
      "Dashly is an AI Inbound Revenue Agent, which covers the inbound side of the AI SDR category. It qualifies website visitors and trial users in real time, books meetings with AEs, and nurtures leads across the lifecycle inside the same platform. It does not do cold email prospecting or outbound sequences. Teams that need both inbound qualification and outbound typically pair Dashly with a tool like AiSDR or Reply.io. The two tools operate on non-overlapping lead cohorts and share data through CRM sync.",
  },
  {
    question: "Do AI SDR tools require annual contracts?",
    answer:
      "It varies widely. Dashly, Apollo.io, and Reply.io offer monthly plans starting at $29-500/mo. AiSDR, Qualified, and 11x.ai typically require annual commitments once you move past a trial, especially at custom-pricing tiers above $900/mo. For Seed and early Series A teams without a validated ICP, prioritize month-to-month options until you have 90 days of conversion data to justify a lock-in.",
  },
  {
    question: "What's the difference between an AI SDR agent and an AI sales agent?",
    answer:
      "An AI SDR agent is scoped to the top of the funnel: qualifying inbound leads or researching outbound prospects, then booking a meeting or handing off to a rep. An AI sales agent is a broader label that can also cover deal support, renewal outreach, or customer success follow-ups. Tools like Dashly, AiSDR, and 11x.ai market themselves as AI SDR agents because they stop at qualification and booking. Platforms that extend into account management or upsell conversations tend to use the wider AI sales agent term instead.",
  },
  {
    question: "Is an AI SDR agent fully autonomous, or does it need human oversight?",
    answer:
      "It depends on the vendor and the workflow. Fully autonomous agents, like AiSDR on outbound email, send messages without a review step, which works for high-volume, low-stakes outreach but risks off-brand replies. Human-in-the-loop agents draft or qualify, then route edge cases, such as custom pricing or security questionnaires, to a rep. For most B2B SaaS teams, a hybrid model catches more edge cases without slowing down simple, high-confidence qualifications.",
  },
];
